import React, { useEffect, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import { History } from 'history';
import { useMatchMedia } from '@guildeducationinc/catalog-tools';
import { ErrorPage } from '@guildeducationinc/recess';
import {
	GetMemberProgramQuery,
	useGetMemberProgramQuery,
} from 'src/graphqlTypes/catalogServiceTypes';
import { AboutProgram } from 'src/components/ProgramDetails/AboutProgram';
import { BreadcrumbBar } from 'src/components/ProgramDetails/BreadcrumbBar';
import { CatalogFeedbackWidget } from 'src/components/CatalogFeedbackWidget';
import { SEO } from 'src/components/SEO/SEO';
import { ProgramDetailsNavigation } from 'src/components/ProgramDetails/ProgramDetailsNavigation';
import { LoadingOverlay } from '@src/components/LoadingOverlay';
import { useHistory } from 'react-router-dom';
import { logDataDogError } from '@src/utilities/datadog';
import { useSearchContext } from '@src/components/Providers/SearchProvider';
import { ProgramHighlightsPanel } from '@src/components/ProgramDetails/ProgramHighlightsPanel';
import { SnapshotHeader } from '@src/components/ProgramDetails/SnapshotHeader';
import {
	GetRolesForProgramsQuery,
	useGetRolesForProgramsQuery,
} from '@src/graphqlTypes/roleProgramGraphServiceTypes';
import { useDecision } from '@optimizely/react-sdk';
import { PROGRAM_COST, ROLE_MAPPING } from '@src/constants/featureFlags';
import { shouldShowCost } from '@src/constants/cost';
import { PricebookProvider } from '@src/components/Providers/PricebookProvider';
import { ROLE_PROGRAM_GRAPH_SERVICE_IDENTIFIER } from '@src/components/Providers/GqlProvider';
import styles from './ProgramDetailsContainer.module.scss';

interface Props {
	match: {
		url: string;
		params: {
			offeringId: string;
		};
	};
	history: History;
}

export const ProgramDetailsContainer: React.FC<Props> = props => {
	const history = useHistory();
	const { offeringId: programId } = props.match.params;
	const isValidProgramId = /^([\d]+)$/.test(programId);
	const [rolesDecision] = useDecision(ROLE_MAPPING);
	const [costDecision] = useDecision(PROGRAM_COST);
	const { employerUuid, memberPlanData, memberUuid } = useSearchContext();
	const [program, setProgram] = useState<GetMemberProgramQuery['memberProgram']>(null);
	const [programLoading, setProgramLoading] = useState(true);
	const [programError, setProgramError] = useState(false);
	const [sections, setSections] = useState<NodeListOf<Element>>();
	const [rolesForPrograms, setRolesForPrograms] = useState<
		GetRolesForProgramsQuery['rolesForPrograms']
	>([]);
	const academicModel = program?.studentFacingAcademicPartner.academicModel;
	const showCost = shouldShowCost(academicModel, costDecision);
	const isDesktop = useMatchMedia('(min-width: 1052px)');
	const [ref, headerInView] = useInView({ threshold: 0 });

	useEffect(() => {
		if (!programLoading && program) {
			const items = document.querySelectorAll('.content-container [data-title]');
			setSections(items);
		}
	}, [programLoading, program, rolesForPrograms, showCost]);

	useEffect(() => {
		if (!programLoading && program) {
			window.ApptentiveSDK.engage('program-page-view', {
				programId: program.uuid,
			});

			// 10 second timeout to be used as a delayed trigger for the program page survey
			const timeout = setTimeout(() => {
				window.ApptentiveSDK.engage('program-page-delay', {
					programId: program.uuid,
				});
			}, 10000);

			return () => clearTimeout(timeout);
		}
	}, [programLoading, program]);

	useGetMemberProgramQuery({
		variables: { programId, memberUuid, employerUuid },
		skip: !employerUuid || !memberUuid || !isValidProgramId,
		onCompleted: data => {
			setProgram(data.memberProgram);
			setProgramLoading(false);
		},
		onError: error => {
			setProgramLoading(false);
			setProgramError(true);
			logDataDogError(error, 'ProgramDetailsError');
		},
	});

	useGetRolesForProgramsQuery({
		context: { gqlServer: ROLE_PROGRAM_GRAPH_SERVICE_IDENTIFIER },
		skip: !rolesDecision.enabled || !program?.uuid,
		variables: {
			employerId: employerUuid,
			programIds: [program?.uuid],
			version: '2',
		},
		onCompleted: data => {
			setRolesForPrograms(data.rolesForPrograms);
		},
		onError: error => {
			logDataDogError(error, 'RolesDataError');
		},
	});

	if (isValidProgramId && !programError && (programLoading || !program)) {
		return <LoadingOverlay />;
	}

	if (programError) {
		return <ErrorPage goBack={history.goBack} message={null} />;
	}

	if (!isValidProgramId) {
		logDataDogError(
			new Error(`Program: ${programId} is not a valid program id`),
			'InvalidProgramIdError',
			{
				catalogMemberTable: memberPlanData,
			}
		);

		return <ErrorPage goBack={history.goBack} message={null} />;
	}

	if (!program && !programLoading) {
		logDataDogError(
			new Error(
				`Program uuid: ${program?.uuid}, program id: ${programId} is not available to member`
			),
			'ProgramUnavailableError',
			{
				catalogMemberTable: memberPlanData,
				id: programId,
				name: program?.name,
				uuid: program?.uuid,
			}
		);

		return <ErrorPage goBack={history.goBack} message={null} />;
	}

	const renderProgramHighlightPanel = (isSticky: boolean) => (
		<ProgramHighlightsPanel isSticky={isSticky} loading={programLoading} program={program} />
	);

	return (
		<PricebookProvider
			academicModel={academicModel}
			academicPartnerUuid={program.studentFacingAcademicPartner.uuid}
			programUuid={program.uuid}
		>
			<div className={styles.programDetailsContainer}>
				<SEO
					path={location.pathname}
					title={program ? `${program.name} at ${program.studentFacingAcademicPartner.name}` : ''}
				/>
				<BreadcrumbBar loading={programLoading} program={program} />
				<div ref={ref}>
					<SnapshotHeader inView={headerInView} loading={programLoading} program={program} />
				</div>
				<div className={styles.bottomContentContainer}>
					<section className={styles.leftContentContainer}>
						{!isDesktop && <div className={styles.panel}>{renderProgramHighlightPanel(false)}</div>}
						<ProgramDetailsNavigation
							availableSections={sections}
							loading={programLoading}
							program={program}
						/>
						<AboutProgram
							loading={programLoading}
							program={program}
							rolesForPrograms={rolesForPrograms}
						/>
						<CatalogFeedbackWidget />
					</section>
					{headerInView && isDesktop && <div className={styles.panelPlaceholder} />}
					{!headerInView && isDesktop && (
						<div className={styles.panel}>{renderProgramHighlightPanel(true)}</div>
					)}
				</div>
			</div>
		</PricebookProvider>
	);
};
